import * as B from '@blueprintjs/core';
import * as turf from '@turf/turf';
import * as I from 'immutable';
import React, {FormEvent} from 'react';

import MemoizedFeatureArea from 'app/components/MemoizedFeatureArea';
import {HoverProperty} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganizationAreaUnit} from 'app/modules/Remote/Organization';
import * as conversionUtils from 'app/utils/conversionUtils';
import {featureM2ToArea} from 'app/utils/featureUtils';

import {
  FeatureId,
  NumberedFeature,
  NumberedFeatureCollection,
} from './ManageFeaturesProvider';
import {DrawPropertiesState} from './ManagePropertiesDraw';
import {LocationRow, PropertyGroup, RowIssue} from './rowStatus';
import cs from './styles.module.scss';

import {ManagePropertiesMode, isDrawnPropertiesMode} from '.';

const FEATURE_TYPES_WITH_NO_AREA = ['Point', 'MultiPoint', 'LineString', 'MultiLineString'];
function isFeatureTypeWithNoArea(value: string): boolean {
  return FEATURE_TYPES_WITH_NO_AREA.includes(value);
}

type SectionKey = 'errors' | 'warnings' | 'ready';

// Self-contained name input: draft lives in local state so typing doesn't churn parent re-renders. Commits on blur or Enter.
const NameInput: React.FC<{
  id?: string;
  initialValue: string;
  onCommit: (value: string) => void;
  className?: string;
}> = ({id, initialValue, onCommit, className}) => {
  const [draft, setDraft] = React.useState(initialValue);
  // Re-sync if the committed value changes externally (file reupload, split, cascade from parent).
  React.useEffect(() => setDraft(initialValue), [initialValue]);
  const commit = () => {
    if (draft !== initialValue) onCommit(draft);
  };
  return (
    <B.InputGroup
      id={id}
      className={className}
      type="text"
      value={draft}
      onChange={(e: FormEvent<HTMLInputElement>) =>
        setDraft((e.currentTarget as HTMLInputElement).value)
      }
      onBlur={commit}
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
      }}
    />
  );
};

export interface Props {
  selectedFeatureIds: I.Set<number>;
  allUploadableFeatureIds: I.Set<number>;
  setSelectedFeatureIds: React.Dispatch<React.SetStateAction<I.Set<number>>>;
  areaUnit: ApiOrganizationAreaUnit;
  nameValues: I.Map<number, string>;
  nameField: string | null;
  setNameValues: React.Dispatch<React.SetStateAction<I.Map<number, string> | null>>;
  partNameValues: I.Map<number, string | null> | null;
  setPartNameValues: React.Dispatch<React.SetStateAction<I.Map<number, string | null> | null>>;
  groupModeByName: I.Map<string, 'group' | 'individual'>;
  setGroupModeForName: (name: string, value: 'group' | 'individual') => void;
  partNameField: string | null;
  setPartNameField: React.Dispatch<React.SetStateAction<string | null>>;
  partNameFieldOptions: string[];
  errorsGroups: PropertyGroup[];
  warningsGroups: PropertyGroup[];
  readyGroups: PropertyGroup[];
  hoveredFeatureIds: I.Set<number>;
  hoverFeatureById: (fId: number | null) => void;
  setSelectedFeature: (value: React.SetStateAction<NumberedFeature | null>) => void;
  setUploadedFeatureCollection: React.Dispatch<
    React.SetStateAction<NumberedFeatureCollection | null>
  >;
  mode: ManagePropertiesMode;
  drawPropertiesState?: DrawPropertiesState;
  removeFeature: (featureId: FeatureId) => void;
  splitFeature?: (featureId: FeatureId) => Promise<void>;
  isConvertingFile?: boolean;
  additionalColumnProperties?: HoverProperty[];
}

const ManageFeaturesTable: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  selectedFeatureIds,
  allUploadableFeatureIds,
  setSelectedFeatureIds,
  nameField,
  areaUnit,
  nameValues,
  setNameValues,
  setPartNameValues,
  groupModeByName,
  setGroupModeForName,
  partNameField,
  setPartNameField,
  partNameFieldOptions,
  errorsGroups,
  warningsGroups,
  readyGroups,
  hoverFeatureById,
  hoveredFeatureIds,
  setSelectedFeature,
  setUploadedFeatureCollection,
  drawPropertiesState,
  removeFeature,
  splitFeature,
  isConvertingFile = false,
  mode,
  additionalColumnProperties = [],
}) => {
  const allGroups = [...errorsGroups, ...warningsGroups, ...readyGroups];
  const noFeaturesHaveArea = allGroups.every((group) =>
    group.locations.every((location) => isFeatureTypeWithNoArea(location.feature.geometry.type))
  );
  const showAreaColumn = !noFeaturesHaveArea && mode !== 'overlay' && mode !== 'updateOverlay';
  // Show the Location Name column once any property spans multiple locations.
  const showLocationColumn = allGroups.some((group) => group.locations.length > 1);
  const canEditShape = isDrawnPropertiesMode(mode);

  const [collapsedSections, setCollapsedSections] = React.useState<Record<SectionKey, boolean>>({
    errors: false,
    warnings: false,
    ready: false,
  });
  // Multi-location parents expanded to their location subrows (by group key).
  const [expandedGroups, setExpandedGroups] = React.useState<I.Set<string>>(I.Set());
  // Location rows expanded to their issue fold-out (by feature id).
  const [expandedIssues, setExpandedIssues] = React.useState<I.Set<FeatureId>>(I.Set());

  // checkbox + name + (location) + (area) + (additional…) + trailing actions
  const columnCount =
    2 +
    (showLocationColumn ? 1 : 0) +
    (showAreaColumn ? 1 : 0) +
    additionalColumnProperties.length +
    1;

  const canRunFix = (issue: RowIssue): boolean =>
    !!issue.action && (issue.action.kind !== 'split' || !!splitFeature);

  const formatArea = (m2: number) =>
    conversionUtils.numberWithCommas(featureM2ToArea(m2, {unit: areaUnit}));

  const removeFeatures = (featureIds: FeatureId[]) => (ev: React.MouseEvent) => {
    ev.stopPropagation();
    const ids = I.Set(featureIds);
    featureIds.forEach(removeFeature);
    setSelectedFeatureIds((selected) => selected.subtract(ids));
    setSelectedFeature((sel) => (sel && ids.has(sel.id) ? null : sel));
    if (drawPropertiesState?.featureToEdit && ids.has(drawPropertiesState.featureToEdit.id as FeatureId)) {
      drawPropertiesState.setFeatureToEdit(null);
    }
  };

  const renderIssue = (location: LocationRow, issue: RowIssue) => {
    const runnable = canRunFix(issue);
    const runFix = () => {
      if (!runnable || !issue.action) return;
      if (issue.action.kind === 'split') splitFeature?.(issue.featureId);
      else document.getElementById(`feature-name-input-${location.feature.id}`)?.focus();
    };
    return (
      <div className={cs.issueRow} key={issue.id}>
        <B.Icon
          icon="full-circle"
          size={8}
          intent={runnable ? B.Intent.WARNING : B.Intent.NONE}
          className={cs.issueDot}
        />
        <div className={cs.issueText}>
          <div className={cs.issueTitle}>{issue.title}</div>
          <div className={cs.issueDescription}>{issue.description}</div>
        </div>
        {runnable && issue.action ? (
          <B.Button
            small
            outlined
            intent={B.Intent.PRIMARY}
            text={issue.action.label}
            disabled={isConvertingFile}
            onClick={(ev: React.MouseEvent) => {
              ev.stopPropagation();
              runFix();
            }}
          />
        ) : (
          <span className={cs.cannotFix}>Cannot be fixed automatically</span>
        )}
      </div>
    );
  };

  // The issues fold-out + a Remove button, shared by single-location rows and
  // location subrows.
  const renderIssuesFoldout = (location: LocationRow) => (
    <tr className={cs.issuesFoldoutRow}>
      <td colSpan={columnCount}>
        <div className={cs.issuesFoldout}>
          {location.issues.map((issue) => renderIssue(location, issue))}
          {location.blocking && (
            <div className={cs.issuesFoldoutFooter}>
              <B.Button
                outlined
                intent={B.Intent.DANGER}
                text="Remove from upload"
                onClick={removeFeatures([location.feature.id])}
              />
            </div>
          )}
        </div>
      </td>
    </tr>
  );

  const areaCell = (feature: NumberedFeature) =>
    showAreaColumn ? (
      <td>
        {isFeatureTypeWithNoArea(feature.geometry.type) ? (
          'n/a'
        ) : (
          <MemoizedFeatureArea
            feature={feature}
            areaUnit={areaUnit}
            formatAreaFn={(areaM2, unit) =>
              conversionUtils.numberWithCommas(featureM2ToArea(areaM2, {unit}))
            }
          />
        )}
      </td>
    ) : null;

  // Committed by NameInput on blur/Enter — writes nameValues, plus the FC name in draw/parcel/agent.
  const setName = (featureIds: FeatureId[], value: string) => {
    setNameValues((prev) => (prev ?? I.Map()).merge(I.Map(featureIds.map((id) => [id, value]))));
    if (isDrawnPropertiesMode(mode)) {
      const ids = I.Set(featureIds);
      setUploadedFeatureCollection((fc) =>
        fc
          ? {
              ...fc,
              features: fc.features.map((f) =>
                ids.has(f.id) ? {...f, properties: {...f.properties, name: value}} : f
              ),
            }
          : fc
      );
    }
  };

  // A single-location property: behaves like a normal row (name input + its own
  // issues fold-out). `extraLeading` injects the chevron/blank alignment cell.
  const renderSingle = (group: PropertyGroup) => {
    const location = group.locations[0];
    const feature = location.feature;
    const featureId = feature.id;
    const selected = selectedFeatureIds.has(featureId);
    const issuesOpen = location.issues.length > 0 && expandedIssues.has(featureId);
    return (
      <React.Fragment key={String(featureId)}>
        <tr
          className={hoveredFeatureIds.has(featureId) ? cs.hoveredRow : undefined}
          onClick={() => selected && setSelectedFeature({...feature})}
          onMouseEnter={() => selected && hoverFeatureById(featureId)}
          onMouseLeave={() => hoverFeatureById(null)}
        >
          <td>
            <B.Checkbox
              className={cs.checkbox}
              checked={selected}
              disabled={location.blocking}
              onChange={() =>
                setSelectedFeatureIds((ids) =>
                  ids.has(featureId) ? ids.remove(featureId) : ids.add(featureId)
                )
              }
            />
          </td>
          <td>
            <div className={cs.nameCell}>
              <NameInput
                id={`feature-name-input-${featureId}`}
                className={cs.nameInput}
                initialValue={nameValues.get(featureId) ?? ''}
                onCommit={(value) => setName([featureId], value)}
              />
            </div>
          </td>
          {showLocationColumn && <td />}
          {areaCell(feature)}
          {additionalColumnProperties.map((ap) => (
            <td key={ap.key}>{feature.properties ? feature.properties[ap.key] : 'n/a'}</td>
          ))}
          <td>
            <div className={cs.rowActions}>
              {canEditShape && (
                <B.Tooltip content={'Edit Shape'} intent={'primary'}>
                  <B.Button
                    icon="polygon-filter"
                    minimal
                    intent={'primary'}
                    onClick={(ev: React.MouseEvent) => {
                      ev.stopPropagation();
                      if (drawPropertiesState) {
                        drawPropertiesState.setEditMode(true);
                        drawPropertiesState.setFeatureToEdit(feature);
                      }
                    }}
                  />
                </B.Tooltip>
              )}
              {location.issues.length > 0 && (
                <B.Button
                  minimal
                  text={`${location.issues.length} ${location.issues.length === 1 ? 'issue' : 'issues'}`}
                  rightIcon={issuesOpen ? 'chevron-up' : 'chevron-down'}
                  onClick={(ev: React.MouseEvent) => {
                    ev.stopPropagation();
                    setExpandedIssues((prev) =>
                      prev.has(featureId) ? prev.remove(featureId) : prev.add(featureId)
                    );
                  }}
                />
              )}
              <B.Tooltip content={'Remove'} intent={'danger'}>
                <B.Button icon="cross" minimal onClick={removeFeatures([featureId])} />
              </B.Tooltip>
            </div>
          </td>
        </tr>
        {issuesOpen && renderIssuesFoldout(location)}
      </React.Fragment>
    );
  };

  // A multi-location property: a parent row (name + N locations + combine/keep
  // control) expanding to location subrows.
  const renderMultiLocation = (group: PropertyGroup) => {
    const featureIds = group.locations.map((l) => l.feature.id);
    const selectedCount = featureIds.filter((id) => selectedFeatureIds.has(id)).length;
    const allSelected = selectedCount === featureIds.length;
    const someSelected = selectedCount > 0 && !allSelected;
    const expanded = expandedGroups.has(group.key);
    const groupMode = groupModeByName.get(group.key) ?? 'group';

    return (
      <React.Fragment key={String(group.locations[0].feature.id)}>
        <tr className={cs.parentRow}>
          <td>
            <B.Checkbox
              className={cs.checkbox}
              checked={allSelected}
              indeterminate={someSelected}
              disabled={group.blocking && selectedCount === 0}
              onChange={() =>
                setSelectedFeatureIds((ids) =>
                  allSelected ? ids.subtract(featureIds) : ids.union(featureIds)
                )
              }
            />
          </td>
          <td>
            <div className={cs.nameCell}>
              <B.Button
                small
                minimal
                icon={expanded ? 'chevron-down' : 'chevron-right'}
                onClick={(ev: React.MouseEvent) => {
                  ev.stopPropagation();
                  setExpandedGroups((prev) =>
                    prev.has(group.key) ? prev.remove(group.key) : prev.add(group.key)
                  );
                }}
              />
              {groupMode === 'group' ? (
                // Combine mode: one shared, editable name (renames all locations).
                <NameInput
                  id={`feature-name-input-${group.locations[0].feature.id}`}
                  className={cs.nameInput}
                  initialValue={nameValues.get(group.locations[0].feature.id) ?? group.name}
                  onCommit={(value) => {
                    setName(featureIds, value);
                    // Groups key on the name, so carry the expanded state to the new one.
                    if (expanded) setExpandedGroups((prev) => prev.add(value));
                  }}
                />
              ) : (
                // Separate mode: names are edited per location in the subrows.
                <span className={cs.parentName}>{group.name}</span>
              )}
              <span className={cs.locationCount}>· {group.locations.length} locations</span>
            </div>
          </td>
          {showLocationColumn && <td />}
          {showAreaColumn && (
            <td>{formatArea(group.locations.reduce((sum, l) => sum + turf.area(l.feature), 0))}</td>
          )}
          {additionalColumnProperties.map((ap) => (
            <td key={ap.key} />
          ))}
          <td>
            <div className={cs.rowActions}>
              <B.Tooltip content={'Remove property'} intent={'danger'}>
                <B.Button icon="cross" minimal onClick={removeFeatures(featureIds)} />
              </B.Tooltip>
            </div>
          </td>
        </tr>
        {expanded && (
          <tr className={cs.groupControlRow}>
            <td />
            <td colSpan={columnCount - 1}>
              <div className={cs.groupControl}>
                <div className={cs.groupControlHint}>
                  In your file, {group.locations.length} properties shared the name “{group.key}”.
                </div>
                <B.RadioGroup
                  selectedValue={groupMode}
                  onChange={(ev) => {
                    const newMode = ev.currentTarget.value as 'group' | 'individual';
                    setGroupModeForName(group.key, newMode);
                    // Combine: fan the shared name across all locations so submit sends one name.
                    if (newMode === 'group') {
                      const sharedName =
                        nameValues.get(group.locations[0].feature.id) ?? group.name;
                      setName(featureIds, sharedName);
                    }
                  }}
                >
                  <B.Radio value="group">
                    <strong>Combine as one multi-location property.</strong> Ideal if you’d like to
                    create a single report for all locations.
                  </B.Radio>
                  <B.Radio value="individual">
                    <strong>Keep as separate properties.</strong> Ideal if you plan to create
                    individual reports — edit each location’s name below to tell them apart.
                    Properties with the exact same name will be grouped together as a
                    multi-location.
                  </B.Radio>
                </B.RadioGroup>
                {groupMode === 'group' && partNameFieldOptions.length > 0 && (
                  <label className={cs.groupControlAttr}>
                    Use a file attribute for location names:{' '}
                    <B.HTMLSelect
                      minimal
                      value={partNameField ?? 'none'}
                      onChange={(ev) => setPartNameField(ev.currentTarget.value)}
                    >
                      <option disabled value="none">
                        Select…
                      </option>
                      {partNameFieldOptions.map((key) => (
                        <option disabled={key === nameField} key={key} value={key}>
                          {key}
                        </option>
                      ))}
                    </B.HTMLSelect>
                  </label>
                )}
              </div>
            </td>
          </tr>
        )}
        {expanded &&
          group.locations.map((location) => renderLocationSubrow(group, location, groupMode))}
      </React.Fragment>
    );
  };

  const renderLocationSubrow = (
    group: PropertyGroup,
    location: LocationRow,
    groupMode: 'group' | 'individual'
  ) => {
    const feature = location.feature;
    const featureId = feature.id;
    const selected = selectedFeatureIds.has(featureId);
    const issuesOpen = location.issues.length > 0 && expandedIssues.has(featureId);
    return (
      <React.Fragment key={String(featureId)}>
        <tr
          className={hoveredFeatureIds.has(featureId) ? cs.hoveredRow : undefined}
          onClick={() => selected && setSelectedFeature({...feature})}
          onMouseEnter={() => selected && hoverFeatureById(featureId)}
          onMouseLeave={() => hoverFeatureById(null)}
        >
          <td>
            <B.Checkbox
              className={cs.checkbox}
              checked={selected}
              disabled={location.blocking}
              onChange={() =>
                setSelectedFeatureIds((ids) =>
                  ids.has(featureId) ? ids.remove(featureId) : ids.add(featureId)
                )
              }
            />
          </td>
          <td>
            <div className={cs.subRowNameCell}>
              <B.Icon icon="key-enter" className={cs.subRowIcon} />
              {groupMode === 'group' ? (
                // Combine mode: the name is shared (read-only); edit the part name.
                <span className={cs.subRowName}>{nameValues.get(featureId) ?? group.name}</span>
              ) : (
                // Separate mode: each location is its own property — name editable.
                <NameInput
                  id={`feature-name-input-${featureId}`}
                  className={cs.nameInput}
                  initialValue={nameValues.get(featureId) ?? ''}
                  onCommit={(value) => setName([featureId], value)}
                />
              )}
            </div>
          </td>
          {showLocationColumn && (
            <td>
              {groupMode === 'group' && (
                <B.InputGroup
                  type="text"
                  className={cs.nameInput}
                  placeholder="Location"
                  value={location.partName}
                  onChange={(event: FormEvent<HTMLInputElement>) => {
                    const {value} = event.currentTarget as HTMLInputElement;
                    setPartNameValues((prev) => (prev ?? I.Map()).set(featureId, value));
                  }}
                />
              )}
            </td>
          )}
          {areaCell(feature)}
          {additionalColumnProperties.map((ap) => (
            <td key={ap.key}>{feature.properties ? feature.properties[ap.key] : 'n/a'}</td>
          ))}
          <td>
            <div className={cs.rowActions}>
              {location.issues.length > 0 && (
                <B.Button
                  minimal
                  text={`${location.issues.length} ${location.issues.length === 1 ? 'issue' : 'issues'}`}
                  rightIcon={issuesOpen ? 'chevron-up' : 'chevron-down'}
                  onClick={(ev: React.MouseEvent) => {
                    ev.stopPropagation();
                    setExpandedIssues((prev) =>
                      prev.has(featureId) ? prev.remove(featureId) : prev.add(featureId)
                    );
                  }}
                />
              )}
              <B.Tooltip content={'Remove location'} intent={'danger'}>
                <B.Button icon="cross" minimal onClick={removeFeatures([featureId])} />
              </B.Tooltip>
            </div>
          </td>
        </tr>
        {issuesOpen && renderIssuesFoldout(location)}
      </React.Fragment>
    );
  };

  const renderGroup = (group: PropertyGroup) =>
    group.locations.length > 1 ? renderMultiLocation(group) : renderSingle(group);

  // Section property count, mode-aware: a combine group counts once, a
  // keep-separate group contributes one per location. The "Ready" section is
  // selection-aware (mirrors the footer — "how many will I upload from
  // here"), while "Needs attention" counts every location in the section
  // (items there are usually deselected because they're blocking, so
  // selection-aware would surface a misleading near-zero count).
  const propertyCountFor = (groups: PropertyGroup[], opts: {selectedOnly: boolean}) => {
    let count = 0;
    for (const group of groups) {
      const locations = opts.selectedOnly
        ? group.locations.filter((l) => selectedFeatureIds.has(l.feature.id))
        : group.locations;
      if (locations.length === 0) continue;
      const mode = groupModeByName.get(group.key) ?? 'group';
      count += mode === 'group' ? 1 : locations.length;
    }
    return count;
  };

  const sectionHeaderClass: Record<SectionKey, string> = {
    errors: cs.sectionHeaderAttention,
    warnings: cs.sectionHeaderWarning,
    ready: cs.sectionHeader,
  };

  // Returns a flat array so every section's rows are direct tbody siblings — a wrapping Fragment would create a reconciliation boundary and lose input focus when a row moves between sections.
  const renderSectionRows = (key: SectionKey, label: string, groups: PropertyGroup[]) => {
    if (groups.length === 0) return null;
    const isCollapsed = collapsedSections[key];
    const headerRow = (
      <tr
        key={`section-${key}`}
        className={sectionHeaderClass[key]}
        onClick={() => setCollapsedSections((prev) => ({...prev, [key]: !prev[key]}))}
      >
        <td colSpan={columnCount}>
          <span className={cs.sectionHeaderLabel}>
            <B.Icon icon={isCollapsed ? 'chevron-right' : 'chevron-down'} />
            {label} · {propertyCountFor(groups, {selectedOnly: key === 'ready'})}
          </span>
        </td>
      </tr>
    );
    return isCollapsed ? [headerRow] : [headerRow, ...groups.map(renderGroup)];
  };

  return (
    <div className={cs.tableWrapper}>
      <table className={cs.table}>
        <thead>
          <tr>
            <th>
              <B.Checkbox
                className={cs.checkbox}
                checked={
                  allUploadableFeatureIds.size > 0 &&
                  selectedFeatureIds.size === allUploadableFeatureIds.size
                }
                onChange={() => {
                  if (selectedFeatureIds.size === allUploadableFeatureIds.size) {
                    setSelectedFeatureIds(I.Set([]));
                  } else {
                    setSelectedFeatureIds(allUploadableFeatureIds);
                  }
                }}
              />
            </th>
            <th>{mode === 'overlay' || mode === 'updateOverlay' ? nameField : 'Property'}</th>
            {showLocationColumn && <th>Location name</th>}
            {showAreaColumn && <th>{areaUnit === 'areaHectare' ? 'Hectares' : 'Acres'}</th>}
            {additionalColumnProperties.map((ap) => (
              <th key={ap.key}>{ap.label}</th>
            ))}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {renderSectionRows('errors', 'Errors', errorsGroups)}
          {renderSectionRows('warnings', 'Warnings', warningsGroups)}
          {renderSectionRows('ready', 'Ready', readyGroups)}
        </tbody>
      </table>
    </div>
  );
};

export default ManageFeaturesTable;
